import { useRef, useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { FileUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ThreeStateToggle,
  type ThreeStateValue,
} from "@/components/ui/three-state-toggle";
import { parseLcSettings, LcSettingsParseError } from "@/utils/lcSettingsToml";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/utils/cn";

interface ParamMeta {
  label: string;
  min: number;
  max: number;
  step: number;
}

const NUMERIC_PARAMS: Record<string, ParamMeta> = {
  neighborscount: { label: "Neighbors Count", min: 5, max: 200, step: 1 },
  maxbarsback: { label: "Max Bars Back", min: 500, max: 40000, step: 50 },
  emaperiod: { label: "EMA Period", min: 10, max: 600, step: 10 },
  smaperiod: { label: "SMA Period", min: 20, max: 600, step: 10 },
  lookbackwindow: { label: "Lookback Window", min: 3, max: 20, step: 1 },
  relativeweight: {
    label: "Relative Weighting",
    min: 1,
    max: 50,
    step: 0.5,
  },
  regressionlevel: { label: "Regression Level", min: 5, max: 75, step: 1 },
  crossoverlag: { label: "Lag", min: 2, max: 8, step: 1 },
  regimethreshold: {
    label: "Regime Threshold",
    min: -2.5,
    max: 2.5,
    step: 0.05,
  },
  adxthreshold: { label: "ADX Threshold", min: 20, max: 40, step: 1 },
};

const BOOLEAN_PARAMS: Record<string, string> = {
  usedynamicexits: "Use Dynamic Exits",
  useemafilter: "Use EMA Filter",
  usesmafilter: "Use SMA Filter",
  usevolatilityfilter: "Use Volatility Filter",
  useregimefilter: "Use Regime Filter",
  useadxfilter: "Use ADX Filter",
};

export interface LcSettingsImportResult {
  parameters: Record<string, number | boolean | [number, number]>;
  pinnedParameters: Record<string, boolean>;
  features: Array<{ name: string; period: number; period2?: number }>;
  toastTitle: string;
  toastDescription: string;
}

interface ImportLcSettingsButtonProps {
  onImport: (result: LcSettingsImportResult) => void;
}

interface EditableValues {
  numeric: Record<string, number | [number, number]>;
  boolean: Record<string, ThreeStateValue>;
}

function initEditableValues(flat: Record<string, unknown>): EditableValues {
  const numeric: Record<string, number | [number, number]> = {};
  for (const key of Object.keys(NUMERIC_PARAMS)) {
    const val = flat[key] as number;
    const range = NUMERIC_PARAMS[key]!;
    if (val < range.min || val > range.max) {
      numeric[key] = val;
    } else {
      numeric[key] = [val, val];
    }
  }
  const bool: Record<string, ThreeStateValue> = {};
  for (const key of Object.keys(BOOLEAN_PARAMS)) {
    bool[key] = (flat[key] as boolean) ? "on" : "off";
  }
  return { numeric, boolean: bool };
}

export function ImportLcSettingsButton({
  onImport,
}: ImportLcSettingsButtonProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [parsedSettings, setParsedSettings] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [editValues, setEditValues] = useState<EditableValues | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 4096) {
      toast({
        title: "File too large",
        description: "LC settings files must be under 4 KB.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    try {
      const text = await file.text();
      const flat = parseLcSettings(text);
      setParsedSettings(flat);
      setEditValues(initEditableValues(flat));
      setDialogOpen(true);
    } catch (err) {
      const message =
        err instanceof LcSettingsParseError
          ? err.message
          : "Could not parse file.";
      toast({
        title: "Invalid LC settings file",
        description: message,
        variant: "destructive",
      });
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRangeChange = useCallback(
    (key: string, idx: 0 | 1, value: number) => {
      setEditValues((prev) => {
        if (!prev) return prev;
        const current = prev.numeric[key];
        if (!Array.isArray(current)) return prev;
        const updated: [number, number] = [...current] as [number, number];
        updated[idx] = value;
        return {
          ...prev,
          numeric: { ...prev.numeric, [key]: updated },
        };
      });
    },
    [],
  );

  const handleNumericChange = useCallback((key: string, value: number) => {
    setEditValues((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        numeric: { ...prev.numeric, [key]: value },
      };
    });
  }, []);

  const handleBooleanChange = useCallback(
    (key: string, value: ThreeStateValue) => {
      setEditValues((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          boolean: { ...prev.boolean, [key]: value },
        };
      });
    },
    [],
  );

  const handleConfirm = () => {
    if (!parsedSettings || !editValues) return;

    const parameters: Record<string, number | boolean | [number, number]> = {};
    const pinnedParameters: Record<string, boolean> = {};
    const outOfRangeWarnings: string[] = [];

    for (const key of Object.keys(NUMERIC_PARAMS)) {
      const val = editValues.numeric[key] ?? 0;
      if (typeof val === "number") {
        parameters[key] = val;
        pinnedParameters[key] = true;
        outOfRangeWarnings.push(key);
      } else {
        parameters[key] = val;
        pinnedParameters[key] = false;
      }
    }

    for (const key of Object.keys(BOOLEAN_PARAMS)) {
      const triState = editValues.boolean[key] ?? "off";
      if (triState === "optimize") {
        parameters[key] = false;
        pinnedParameters[key] = false;
      } else {
        parameters[key] = triState === "on";
        pinnedParameters[key] = true;
      }
    }

    const features: LcSettingsImportResult["features"] = [];
    for (let i = 1; i <= 5; i++) {
      features.push({
        name: parsedSettings[`f${i}_type`] as string,
        period: parsedSettings[`f${i}_period`] as number,
        period2: parsedSettings[`f${i}_multiplier`] as number,
      });
    }

    const kernelSmoothing = parsedSettings.usekernelsmoothing as boolean;
    const parts = ["Settings imported."];
    if (!kernelSmoothing) {
      parts.push("Note: kernel smoothing is always enabled in the optimizer.");
    }
    if (outOfRangeWarnings.length > 0) {
      parts.push(
        `${outOfRangeWarnings.join(", ")} ${outOfRangeWarnings.length === 1 ? "is" : "are"} outside the optimization range and pinned.`,
      );
    }
    const toastDescription = parts.join(" ");

    onImport({
      parameters,
      pinnedParameters,
      features,
      toastTitle: "LC settings imported",
      toastDescription,
    });
    toast({ title: "LC settings imported", description: toastDescription });
    setDialogOpen(false);
    setParsedSettings(null);
    setEditValues(null);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".toml"
        className="hidden"
        onChange={handleFileSelect}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
      >
        <FileUp className="h-4 w-4 mr-2" />
        Import LC Settings
      </Button>

      <DialogPrimitive.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <AnimatePresence>
          {dialogOpen && editValues && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              </DialogPrimitive.Overlay>
              <DialogPrimitive.Content asChild>
                <motion.div
                  className={cn(
                    "fixed left-[50%] top-[50%] z-50 w-full max-w-lg max-h-[80vh] flex flex-col",
                    "bg-dark-900 backdrop-blur-xl border border-dark-700 rounded-xl shadow-2xl",
                  )}
                  initial={{
                    opacity: 0,
                    scale: 0.95,
                    x: "-50%",
                    y: "-48%",
                  }}
                  animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                  exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-48%" }}
                  transition={{ type: "spring", duration: 0.3 }}
                >
                  {/* Header */}
                  <div className="p-6 pb-3 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <DialogPrimitive.Title className="text-lg font-semibold">
                          Import LC Settings
                        </DialogPrimitive.Title>
                        <DialogPrimitive.Description className="text-sm text-muted-foreground mt-1">
                          Review and adjust parameters before importing.
                        </DialogPrimitive.Description>
                      </div>
                      <DialogPrimitive.Close asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </DialogPrimitive.Close>
                    </div>
                  </div>

                  {/* Scrollable parameter list */}
                  <div className="overflow-y-auto flex-1 px-6">
                    {/* Numeric parameters */}
                    <div className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">
                      Parameters
                    </div>
                    <div className="space-y-2">
                      {Object.entries(NUMERIC_PARAMS).map(([key, meta]) => {
                        const val = editValues.numeric[key] ?? 0;
                        const isRange = Array.isArray(val);
                        const isOutOfRange = !isRange;

                        return (
                          <div
                            key={key}
                            className={cn(
                              "flex items-center justify-between py-2 px-3 rounded-lg border",
                              isOutOfRange
                                ? "border-amber-500/30 bg-amber-500/5"
                                : "border-dark-700 bg-dark-800/30",
                            )}
                          >
                            <span className="text-sm font-medium truncate">
                              {meta.label}
                            </span>

                            {isRange ? (
                              <div className="flex items-center gap-1 ml-2">
                                <input
                                  type="number"
                                  value={val[0]}
                                  onChange={(e) =>
                                    handleRangeChange(
                                      key,
                                      0,
                                      parseFloat(e.target.value) || meta.min,
                                    )
                                  }
                                  min={meta.min}
                                  max={val[1]}
                                  step={meta.step}
                                  className="w-20 rounded bg-dark-800 border border-dark-600 px-2 py-1 text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-brand-blue/50"
                                />
                                <span className="text-xs text-muted-foreground">
                                  –
                                </span>
                                <input
                                  type="number"
                                  value={val[1]}
                                  onChange={(e) =>
                                    handleRangeChange(
                                      key,
                                      1,
                                      parseFloat(e.target.value) || meta.max,
                                    )
                                  }
                                  min={val[0]}
                                  max={meta.max}
                                  step={meta.step}
                                  className="w-20 rounded bg-dark-800 border border-dark-600 px-2 py-1 text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-brand-blue/50"
                                />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 ml-2">
                                <input
                                  type="number"
                                  value={val as number}
                                  onChange={(e) =>
                                    handleNumericChange(
                                      key,
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  step={meta.step}
                                  className="w-20 rounded bg-dark-800 border border-amber-500/30 px-2 py-1 text-sm text-right font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                                />
                                <span className="text-[10px] text-amber-500">
                                  pinned (out of range)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Boolean parameters */}
                    <div className="mt-5 space-y-3 pb-2">
                      <div className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">
                        Filters
                      </div>
                      {Object.entries(BOOLEAN_PARAMS).map(([key, label]) => (
                        <ThreeStateToggle
                          key={key}
                          id={`import-${key}`}
                          label={label}
                          value={editValues.boolean[key] ?? "off"}
                          onChange={(val) => handleBooleanChange(key, val)}
                        />
                      ))}
                    </div>

                    {/* Features (read-only display) */}
                    {parsedSettings && (
                      <div className="mt-5 space-y-2 pb-2">
                        <div className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-2">
                          Features
                        </div>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between py-2 px-3 rounded-lg border border-dark-700 bg-dark-800/30"
                          >
                            <span className="text-sm font-medium">
                              Feature {i}
                            </span>
                            <span className="text-sm font-mono text-muted-foreground">
                              {String(parsedSettings[`f${i}_type`])}{" "}
                              {String(parsedSettings[`f${i}_period`])} /{" "}
                              {String(parsedSettings[`f${i}_multiplier`])}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="p-6 pt-4 flex-shrink-0 border-t border-dark-700">
                    <div className="flex justify-end gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        className="bg-dark-800/50 border-dark-700"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleConfirm}
                        className="bg-gradient-to-r from-brand-blue to-brand-blue-dark"
                      >
                        Import
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>
    </>
  );
}
